import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpDown, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { GlassInput } from './GlassInput';
import { GlassButton } from './GlassButton';
import { cn } from '../../utils/formatters';

export const GlassTable = ({
  columns,
  data,
  className,
  pagination = true,
  pageSize = 10,
  searchable = true,
  sortable = true,
  onRowClick,
  rowClassName,
  emptyMessage = 'Nenhum dado encontrado',
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');

  // Filter data based on search query
  const filteredData = searchQuery
    ? data.filter((row) =>
        columns.some((col) => {
          const value = col.accessor ? row[col.accessor] : null;
          return value && String(value).toLowerCase().includes(searchQuery.toLowerCase());
        })
      )
    : data;

  // Sort data
  const sortedData = sortConfig.key
    ? [...filteredData].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      })
    : filteredData;

  // Paginate data
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = pagination
    ? sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : sortedData;

  const handleSort = (key) => {
    if (!sortable) return;
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  return (
    <div className="w-full">
      {/* Search */}
      {searchable && (
        <div className="mb-4">
          <GlassInput
            placeholder="Buscar..."
            icon={Search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />
        </div>
      )}

      {/* Table */}
      <div className={cn(
        'overflow-hidden rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-xl',
        className
      )}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10 bg-white/[0.02]">
                {columns.map((column) => (
                  <th
                    key={column.key || column.accessor}
                    className={cn(
                      'px-6 py-4 text-left text-xs font-semibold text-white/70 uppercase tracking-wider',
                      sortable && column.accessor && 'cursor-pointer hover:text-white/90'
                    )}
                    onClick={() => column.accessor && handleSort(column.accessor)}
                  >
                    <div className="flex items-center gap-2">
                      {column.title}
                      {sortable && column.accessor && (
                        <ArrowUpDown className={cn(
                          'w-3.5 h-3.5 transition-colors',
                          sortConfig.key === column.accessor ? 'text-accent-primary' : 'text-white/30'
                        )} />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {paginatedData.length > 0 ? (
                paginatedData.map((row, index) => (
                  <motion.tr
                    key={row.id || index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={cn(
                      'transition-colors hover:bg-white/[0.03]',
                      onRowClick && 'cursor-pointer',
                      rowClassName && rowClassName(row)
                    )}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key || column.accessor}
                        className="px-6 py-4 text-sm text-white/80"
                      >
                        {column.render
                          ? column.render(row)
                          : column.accessor
                          ? row[column.accessor]
                          : null}
                      </td>
                    ))}
                  </motion.tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-6 py-12 text-center text-white/50"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-white/[0.02]">
            <span className="text-sm text-white/50">
              Mostrando {(currentPage - 1) * pageSize + 1} a{' '}
              {Math.min(currentPage * pageSize, sortedData.length)} de {sortedData.length} resultados
            </span>
            <div className="flex items-center gap-2">
              <GlassButton
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                icon={ChevronLeft}
              >
                Anterior
              </GlassButton>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => {
                    if (totalPages <= 5) return true;
                    if (p === 1 || p === totalPages) return true;
                    if (Math.abs(p - currentPage) <= 1) return true;
                    return false;
                  })
                  .map((p, i, arr) => (
                    <React.Fragment key={p}>
                      {i > 0 && arr[i - 1] !== p - 1 && (
                        <span className="px-2 text-white/30">...</span>
                      )}
                      <button
                        onClick={() => setCurrentPage(p)}
                        className={cn(
                          'w-8 h-8 rounded-lg text-sm font-medium transition-colors',
                          currentPage === p
                            ? 'bg-accent-primary text-white'
                            : 'text-white/60 hover:bg-white/10'
                        )}
                      >
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
              </div>
              <GlassButton
                variant="ghost"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                icon={ChevronRight}
                iconPosition="right"
              >
                Próxima
              </GlassButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlassTable;
